import React, { Component } from 'react';
import SideMenu from '../admin/SideMenu';
import TopMenu from '../admin/TopBar';
import { appDatabasePrimaryFunctions } from '../../base';
import '../../styles/css/AdminMain.css';
import { ErrorMessage, Toast, convertTimeStampToHumanReadable, readApiResponse } from '../utils/HelpfulFunction';
import { parseRosterRows, matchableCount, emailCount } from '../utils/rosterParser';
import { teamIdOptions } from '../utils/experienceScope';
import Swal from 'sweetalert2';
import { Modal } from 'reactstrap';

// Verification — season-ticket-member rosters per team. Fans tap Verify
// Status in the app, log in with Ticketmaster, and we match their verified
// email or account number against the roster uploaded here. A match grants
// the season-ticket status (entitlements/{uid}/{teamId}) until season end.

// API URL for Firebase Functions
let apiUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api/`;
if (process.env.NODE_ENV === "development") {
    apiUrl = `http://localhost:5001/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/us-central1/api/`;
}

class SetUpVerification extends Component {
    constructor(props) {
        super(props);
        this.state = {
            experiences: {},
            sthRoster: {},
            audit: [],
            loading: true,
            teamId: '',
            statusLabel: '',
            accountGroupCode: '',
            accountGroupId: '',
            identitySource: '',
            rosterModal: false,
            rosterMembers: [],
            rosterFilter: '',
            seasonEndDate: '',
            rosterText: ''
        };
        this.handleChange = this.handleChange.bind(this);
        this.handleTeamChange = this.handleTeamChange.bind(this);
        this.uploadRoster = this.uploadRoster.bind(this);
        this.saveAccountGroup = this.saveAccountGroup.bind(this);
        this.saveIdentitySource = this.saveIdentitySource.bind(this);
        this.handleCsvFile = this.handleCsvFile.bind(this);
        this.viewRoster = this.viewRoster.bind(this);
        this.toggleRosterModal = this.toggleRosterModal.bind(this);
        this.saveRosterChanges = this.saveRosterChanges.bind(this);
    }

    async componentDidMount() {
        await this.loadData();
    }

    async loadData() {
        try {
            const [experiencesSnap, sthRosterSnap, auditSnap] = await Promise.all([
                appDatabasePrimaryFunctions.ref('experiences').once('value'),
                appDatabasePrimaryFunctions.ref('sthRoster').once('value'),
                // Most recent attempts only — the node is append-only and unpruned
                appDatabasePrimaryFunctions.ref('verificationAudit').limitToLast(50).once('value')
            ]);
            const experiences = experiencesSnap.val() || {};
            const sthRoster = sthRosterSnap.val() || {};
            const audit = Object.entries(auditSnap.val() || {})
                .map(([id, entry]) => ({ id, ...entry }))
                .sort((a, b) => (b.at || 0) - (a.at || 0));
            this.setState({ experiences, sthRoster, audit, loading: false }, () => {
                // The dropdown never carries an empty value — default to the
                // first team so the form always targets a real experience id
                const teamList = this.teamExperienceList();
                if (!this.state.teamId && teamList.length > 0) {
                    this.selectTeam(teamList[0].id);
                } else if (this.state.teamId) {
                    this.selectTeam(this.state.teamId);
                }
            });
        } catch (e) {
            console.log('Error loading verification data:', e);
            this.setState({ loading: false });
        }
    }

    // Teams are experiences typed 'team'. A club with none — Checkers and
    // Winterhawks today — gets one synthetic option standing for the club
    // itself, because a season ticket roster is a fact about the club and never
    // needed the experience concept. The old fallback here was to ALL
    // experiences, which still returned nothing for a club that has none, so
    // the screen refused itself.
    teamExperienceList() {
        // Exactly what Offers, User Info and Push offer. This screen used to
        // keep its own legacy fallback, which is how the four came to disagree
        // about what a teamId is on an untyped setup.
        return teamIdOptions(Object.entries(this.state.experiences).map(([id, exp]) => ({ id, ...exp })));
    }

    handleChange(evt) {
        const target = evt.target;
        this.setState({ [target.name]: target.type === 'checkbox' ? target.checked : target.value });
    }

    handleTeamChange(evt) {
        this.selectTeam(evt.target.value);
    }

    // Selecting a team prefills the form from its existing roster meta
    selectTeam(teamId) {
        const roster = this.state.sthRoster[teamId] || {};
        const meta = roster.meta || {};
        this.setState({
            teamId: teamId,
            statusLabel: meta.label || '',
            accountGroupCode: meta.accountGroupCode || '',
            accountGroupId: meta.accountGroupId || '',
            identitySource: meta.identitySource || '',
            seasonEndDate: meta.expiresAt ? this.epochToDateInput(meta.expiresAt) : '',
            rosterText: ''
        });
    }

    // Which identities this team's roster will be matched against.
    //
    // Its own control and its own save, because the account-group form above
    // refuses without a group code — and a club with no Ticketmaster
    // relationship has none, which is exactly the club this setting is for.
    async saveIdentitySource(identitySource) {
        const { teamId } = this.state;
        if (!teamId) return;
        let token = null;
        try {
            token = this.props.userData ? await this.props.userData.getIdToken() : null;
        } catch (e) {
            console.log('Error refreshing auth token:', e);
        }
        if (!token) {
            ErrorMessage.fire({ title: 'Not authenticated', text: 'Could not get an auth token, refresh the page and try again.' });
            return;
        }
        this.setState({ loading: true, identitySource });
        try {
            const response = await fetch(apiUrl + 'setAccountGroup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ teamId, identitySource: identitySource || 'ticketmaster' })
            });
            const result = await readApiResponse(response);
            if (!response.ok) {
                ErrorMessage.fire({ title: 'Could not save', text: (result && result.error) || 'Something went wrong.' });
            } else {
                await this.loadData();
                Toast.fire({ title: 'Saved' });
            }
        } catch (e) {
            console.log(e);
            ErrorMessage.fire({ title: 'Could not save', text: 'Something went wrong saving that setting.' });
        }
        this.setState({ loading: false });
    }

    // ms epoch -> "YYYY-MM-DD" for the date input (local time)
    epochToDateInput(ms) {
        const d = new Date(ms);
        const pad = (n) => (n < 10 ? '0' + n : '' + n);
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }

    // "YYYY-MM-DD" -> end-of-day ms epoch (local time) so the status lasts
    // through the final day of the season
    dateInputToEndOfDayEpoch(dateStr) {
        const parts = (dateStr || '').split('-');
        if (parts.length !== 3) return null;
        const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10), 23, 59, 59, 999);
        return isNaN(d.getTime()) ? null : d.getTime();
    }

    // Parsing lives in utils/rosterParser.js so it can be unit-tested against
    // the shapes real CSV exports arrive in.
    // The uploaded roster, read back so it can be edited.
    //
    // It is stored as two maps keyed for LOOKUP, not for display —
    // sthRosterAccounts/{team}/{acct} and sthRosterEmails/{team}/{emailKey},
    // where the email entry's VALUE is the account number it came in with.
    // Rebuilding the rows means walking the emails first and then picking up
    // any account that no email claimed, or a member with no email address
    // would vanish from a list they are really on.
    static keyToEmail(key) {
        return String(key).replace(/%([0-9A-Fa-f]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
    }

    async viewRoster() {
        const { teamId } = this.state;
        if (!teamId) {
            ErrorMessage.fire({ title: 'No team selected', text: 'Pick which team\'s roster you want to see first.' });
            return;
        }
        this.setState({ loading: true });
        try {
            const [accountsSnap, emailsSnap] = await Promise.all([
                appDatabasePrimaryFunctions.ref(`sthRosterAccounts/${teamId}`).once('value'),
                appDatabasePrimaryFunctions.ref(`sthRosterEmails/${teamId}`).once('value')
            ]);
            const accounts = accountsSnap.val() || {};
            const emails = emailsSnap.val() || {};
            const members = [];
            const claimed = new Set();
            Object.entries(emails).forEach(([key, value]) => {
                const account = typeof value === 'string' ? value : '';
                if (account) claimed.add(account);
                members.push({ account, email: SetUpVerification.keyToEmail(key) });
            });
            Object.keys(accounts).forEach(account => {
                if (!claimed.has(account)) members.push({ account, email: '' });
            });
            members.sort((a, b) => (a.account || '').localeCompare(b.account || '') || a.email.localeCompare(b.email));
            this.setState({ rosterMembers: members, rosterFilter: '', rosterModal: true, loading: false });
        } catch (e) {
            console.log('Error reading roster:', e);
            this.setState({ loading: false });
            ErrorMessage.fire({ title: 'Could not read the roster', text: 'Check your connection and try again.' });
        }
    }

    toggleRosterModal() {
        this.setState({ rosterModal: !this.state.rosterModal });
    }

    // Removing here only edits the list on screen. Nothing is written until
    // Save, so a mis-click costs nothing and the whole edit lands in one
    // upload — which is also what sweeps the removed members' statuses.
    removeRosterMember(index) {
        this.setState({ rosterMembers: this.state.rosterMembers.filter((_, i) => i !== index) });
    }

    async saveRosterChanges() {
        const { teamId, rosterMembers, sthRoster } = this.state;
        const existingCount = ((sthRoster[teamId] || {}).meta || {}).rowCount || 0;
        const removed = existingCount - rosterMembers.length;
        if (removed <= 0) {
            this.setState({ rosterModal: false });
            return;
        }
        const clearing = rosterMembers.length === 0;
        const result = await Swal.fire({
            title: clearing ? 'Remove everyone from this roster?' : `Remove ${removed} member(s)?`,
            text: clearing
                ? 'This empties the roster. Anyone verified from it loses their season ticket status.'
                : `The remaining ${rosterMembers.length} member(s) stay. Anyone removed who was verified from this roster loses their season ticket status.`,
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: clearing ? 'Remove everyone' : 'Remove them'
        });
        if (!result.value) return;
        await this.postRoster(rosterMembers.map(m => ({ account: m.account, email: m.email })), { allowEmpty: clearing });
        this.setState({ rosterModal: false });
    }

    parseRosterRows(rosterText) {
        return parseRosterRows(rosterText);
    }

    handleCsvFile(evt) {
        const file = evt.target.files && evt.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const text = typeof reader.result === 'string' ? reader.result : '';
            const parsed = this.parseRosterRows(text);
            if (parsed.length === 0) {
                ErrorMessage.fire({ title: 'No members found in file', text: 'Could not find account numbers or emails in that CSV. Expected columns: account number, email.' });
                return;
            }
            // Load into the review box so the admin can eyeball before uploading.
            // Lead with the number that decides whether this roster can verify
            // anyone — the account numbers — because a file full of names and
            // emails looks perfectly healthy right up until nobody can verify.
            this.setState({ rosterText: text });
            const withAccounts = matchableCount(parsed);
            Toast.fire({
                title: `Loaded ${parsed.length} member(s) from ${file.name} — ${withAccounts} with account numbers, ${emailCount(parsed)} with emails. Review below, then Upload Roster`
            });
        };
        reader.onerror = () => {
            ErrorMessage.fire({ title: 'Could not read file', text: 'Something went wrong reading that file — try again or paste the rows instead.' });
        };
        reader.readAsText(file);
        // Allow re-selecting the same file
        evt.target.value = '';
    }

    // Saving the account group is deliberately separate from uploading a
    // roster: a team verified live by Ticketmaster has no CSV at all, and
    // making them paste one to save a group code would be nonsense.
    async saveAccountGroup() {
        const { teamId, statusLabel, accountGroupCode, accountGroupId, seasonEndDate } = this.state;
        if (!teamId) {
            ErrorMessage.fire({ title: 'No team selected', text: 'Pick which team this account group belongs to first.' });
            return;
        }
        if (accountGroupId && !/^\d+$/.test(String(accountGroupId).trim())) {
            ErrorMessage.fire({ title: 'Account Group ID must be a number', text: 'Enter the numeric id Ticketmaster lists for the group, e.g. 85.' });
            return;
        }
        if (!accountGroupCode && !accountGroupId) {
            // Saving nothing at all silently turns live verification off, which
            // is a real thing an admin might want — so confirm rather than block.
            const result = await Swal.fire({
                title: 'Clear this team\'s account group?',
                text: 'With no code and no ID, fans can only be verified from an uploaded roster.',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Clear it'
            });
            if (!result.value) return;
        }
        if (!seasonEndDate) {
            ErrorMessage.fire({ title: 'Missing season end date', text: 'Set the Season End Date — without it, verified statuses would never expire.' });
            return;
        }
        let token = null;
        try {
            token = this.props.userData ? await this.props.userData.getIdToken() : null;
        } catch (e) {
            console.log('Error refreshing auth token:', e);
        }
        if (!token) {
            ErrorMessage.fire({ title: 'Not authenticated', text: 'Could not get an auth token, refresh the page and try again.' });
            return;
        }
        this.setState({ loading: true });
        try {
            const response = await fetch(apiUrl + "setAccountGroup", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    teamId: teamId,
                    accountGroupCode: (accountGroupCode || '').trim(),
                    accountGroupId: (accountGroupId || '').trim(),
                    label: (statusLabel || '').trim(),
                    expiresAt: this.dateInputToEndOfDayEpoch(seasonEndDate)
                })
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                ErrorMessage.fire({ title: 'Could not save account group', text: data.error || 'Something went wrong, try again.' });
                return;
            }
            Toast.fire({ title: 'Account group saved' });
            await this.loadData();
            this.selectTeam(teamId);
        } catch (error) {
            console.log('Error saving account group:', error);
            ErrorMessage.fire({ title: 'Could not save account group', text: 'Something went wrong, try again.' });
        } finally {
            this.setState({ loading: false });
        }
    }

    async uploadRoster() {
        const { teamId, statusLabel, seasonEndDate, rosterText } = this.state;
        if (!teamId) {
            ErrorMessage.fire({ title: 'No team selected', text: 'Pick which team this roster belongs to first.' });
            return;
        }
        const rows = this.parseRosterRows(rosterText);
        if (rows.length === 0) {
            ErrorMessage.fire({ title: 'No members found', text: 'Paste at least one season ticket member — one per line: accountNumber,email (email required for matching).' });
            return;
        }
        if (!seasonEndDate) {
            ErrorMessage.fire({ title: 'Missing season end date', text: 'Set the Season End Date — without it, verified statuses would never expire.' });
            return;
        }
        // Say it BEFORE the upload, not after. A roster with no account numbers
        // verifies nobody, and the upload below replaces the existing roster
        // wholesale — so warning afterwards means the working roster is already
        // gone. Usually this means the export's account column was not
        // recognised, which a header row fixes.
        const withAccounts = matchableCount(rows);
        if (withAccounts === 0) {
            const proceed = await Swal.fire({
                title: 'No account numbers in this roster',
                text: `None of your ${rows.length} row(s) carry an account number, and fans are matched on the account number Ticketmaster reports for them — so this roster will not verify anyone. Re-export it including the account column (Acct ID / Account Number), ideally with a header row naming it.`,
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Upload anyway'
            });
            if (!proceed.value) return;
        }

        // Replacing an existing roster is destructive — confirm first
        const existingMeta = (this.state.sthRoster[teamId] || {}).meta;
        if (existingMeta && existingMeta.rowCount) {
            const result = await Swal.fire({
                title: 'Replace existing roster?',
                text: `This team already has a roster of ${existingMeta.rowCount} members (uploaded ${existingMeta.uploadedAt ? new Date(existingMeta.uploadedAt).toLocaleDateString() : 'earlier'}). Uploading replaces it entirely with your ${rows.length} pasted member(s).`,
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Replace'
            });
            if (!result.value) return;
        }
        // A fresh ID token is fetched inside postRoster — the one cached at
        // mount expires after ~an hour and would 401 a long editing session.
        const expiresAt = this.dateInputToEndOfDayEpoch(seasonEndDate);
        await this.postRoster(rows, { expiresAt });
    }

    // The one place a roster is written. Both the paste-and-upload path and
    // the edit-the-list path come through here, so the sweep that revokes
    // dropped members, the warnings and the reload behave identically however
    // the admin got there.
    async postRoster(rows, { expiresAt, allowEmpty = false } = {}) {
        const { teamId, statusLabel, seasonEndDate } = this.state;
        const expiry = expiresAt !== undefined ? expiresAt : this.dateInputToEndOfDayEpoch(seasonEndDate);
        let token = null;
        try {
            token = this.props.userData ? await this.props.userData.getIdToken() : null;
        } catch (e) {
            console.log('Error refreshing auth token:', e);
        }
        if (!token) {
            ErrorMessage.fire({ title: 'Not authenticated', text: 'Could not get an auth token, refresh the page and try again.' });
            return;
        }
        this.setState({ loading: true });
        try {
            const response = await fetch(apiUrl + "uploadSthRoster", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    teamId: teamId,
                    label: (statusLabel || '').trim(),
                    accountGroupCode: (this.state.accountGroupCode || '').trim(),
                    accountGroupId: (this.state.accountGroupId || '').trim(),
                    expiresAt: expiry,
                    rows: rows,
                    allowEmpty: allowEmpty
                })
            });
            const { ok, data, error } = await readApiResponse(response);
            if (ok) {
                const swept = data.revoked
                    ? ` · ${data.revoked} member(s) no longer listed lost their status`
                    : '';
                Toast.fire({
                    title: rows.length === 0
                        ? `Roster cleared${swept}`
                        : `Roster saved — ${data.accounts} account numbers, ${data.emails} emails${swept}`
                });
                this.setState({ rosterText: '' });
                await this.loadData();
                // A fan is matched on the identity Ticketmaster reports to our
                // server, and that identity carries an ACCOUNT NUMBER. An email
                // only matches if Ticketmaster returns one for that member, so a
                // roster without account numbers verifies nobody. Exactly one
                // warning fires — two ErrorMessage.fire calls stomp each other,
                // and the operator would be left reading whichever won.
                // The none-at-all case was already put to the operator before
                // the upload, where they could still back out — repeating it
                // here would just nag about a decision they made deliberately.
                if (data.accounts && data.accounts < rows.length) {
                    ErrorMessage.fire({
                        title: `${rows.length - data.accounts} member(s) have no account number`,
                        text: `${data.accounts} of ${rows.length} rows can be matched today. The rest are stored, and will start matching if Ticketmaster begins returning members' email addresses.`
                    });
                }
            } else {
                ErrorMessage.fire({ title: 'Upload failed', text: error });
                this.setState({ loading: false });
            }
        } catch (e) {
            console.log('Error uploading roster:', e);
            ErrorMessage.fire({ title: 'Upload failed', text: 'Something went wrong uploading the roster, check your connection and try again.' });
            this.setState({ loading: false });
        }
    }

    // What actually happened on an attempt, in the words support needs.
    outcomeLabel(entry) {
        if (entry.outcome === 'rejected') return `Rejected — ${entry.reason || 'unknown reason'}`;
        if (entry.outcome === 'blocked') return `Blocked — already linked to another account (${entry.blockedTeams || '—'})`;
        if (entry.statusesGranted > 0) return `Verified — ${entry.statusesGranted} status(es) granted`;
        return 'No match on any roster';
    }

    render() {
        const { sthRoster, teamId, audit = [] } = this.state;
        const teamList = this.teamExperienceList();
        const selectedRoster = sthRoster[teamId] || {};
        const selectedMeta = selectedRoster.meta || null;
        const selectedTeam = teamList.find(exp => exp.id === teamId);
        const selectedTeamName = selectedTeam ? selectedTeam.name : '';

        // The roster list, filtered for display. The index travels with each
        // row because Remove works on the real list, not on the filtered view —
        // removing by filtered position deletes the wrong person the moment
        // anything is typed in the search box.
        const rosterFilter = (this.state.rosterFilter || '').trim().toLowerCase();
        const matchingRosterMembers = this.state.rosterMembers
            .map((member, index) => ({ member, index }))
            .filter(({ member }) => !rosterFilter
                || (member.account || '').toLowerCase().includes(rosterFilter)
                || (member.email || '').toLowerCase().includes(rosterFilter));
        const ROSTER_PAGE = 200;
        const matchingRosterCount = matchingRosterMembers.length;
        const visibleRosterMembers = matchingRosterMembers.slice(0, ROSTER_PAGE);
        const hiddenRosterCount = matchingRosterCount - visibleRosterMembers.length;
        return (
            <div className="admin-wrapper">
                <div className="loading-screen" style={{ display: this.state.loading ? 'block' : 'none' }}/>
                <SideMenu/>
                <TopMenu/>
                <div className="admin-main-panel">
                    <p className="admin-page-eyebrow">Access</p>
                    <p className="admin-header-text">Verification</p>
                    <p style={{ fontSize: 13, color: 'grey', fontWeight: 'normal', maxWidth: 800, marginLeft: 20 }}>
                        Fans tap Verify Status in the app and log in with Ticketmaster. We match their verified
                        email or account number against the roster uploaded here and grant them the season ticket
                        member status until the season end date.
                    </p>

                    {/* There is no "no teams" state any more: a club with no
                        experiences gets one option standing for itself, because
                        a season ticket roster is a fact about the club. This
                        used to say "Create a team experience first", which sent
                        a club that only wanted to upload a list off to build a
                        second app skin it had no use for. */}
                    {teamList.length > 0 &&
                        <div className="admin-form-box" style={{ maxWidth: 640 }}>
                            <p style={{ fontWeight: 'bold', fontSize: 16 }}>Season ticket roster</p>
                            <div className="form-group" style={{ maxWidth: 520 }}>
                                <label htmlFor="identitySource">Who can be matched against this list</label>
                                <p className="admin-hint">
                                    A fan is only ever granted on an identity someone other than their phone
                                    has confirmed. Ticketmaster is one. A fan's own email address is another,
                                    once they have clicked the confirmation link the app sends them — which is
                                    what lets a club verify its own members without a Ticketmaster relationship.
                                </p>
                                <select
                                    id="identitySource"
                                    name="identitySource"
                                    className="form-control"
                                    value={this.state.identitySource || 'ticketmaster'}
                                    onChange={(e) => this.saveIdentitySource(e.target.value)}
                                >
                                    <option value="ticketmaster">Ticketmaster only</option>
                                    <option value="account-email">Ticketmaster, or a fan's confirmed email address</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="teamId">Team</label>
                                <p style={{ fontSize: 12, fontWeight: 'normal', color: 'grey' }}>Which team this roster belongs to</p>
                                <select id="teamId" name="teamId" className="form-control" style={{ maxWidth: 300 }} value={teamId} onChange={this.handleTeamChange}>
                                    {teamList.map(exp => (
                                        <option key={exp.id} value={exp.id}>{exp.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="statusLabel">Status Label</label>
                                <p style={{ fontSize: 12, fontWeight: 'normal', color: 'grey' }}>What verified fans see on their profile</p>
                                <input type="text" id="statusLabel" name="statusLabel" className="form-control" value={this.state.statusLabel} onChange={this.handleChange} placeholder="2025-26 Season Ticket Member"/>
                            </div>
                            <div className="form-group">
                                <label htmlFor="accountGroupCode">Ticketmaster Account Group</label>
                                <p style={{ fontSize: 12, fontWeight: 'normal', color: 'grey' }}>
                                    The group Ticketmaster assigns to your season ticket members' accounts. It comes as a short code, a numeric id, or both —
                                    enter whichever Ticketmaster gave you. With this set, verification asks Ticketmaster directly and no roster is needed.
                                </p>
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                    <input type="text" id="accountGroupCode" name="accountGroupCode" className="form-control" style={{ maxWidth: 200 }} value={this.state.accountGroupCode} onChange={this.handleChange} placeholder="STM"/>
                                    <input type="text" id="accountGroupId" name="accountGroupId" className="form-control" style={{ maxWidth: 160 }} value={this.state.accountGroupId} onChange={this.handleChange} placeholder="100"/>
                                </div>
                                <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={this.saveAccountGroup}>Save Account Group</button>
                            </div>
                            <div className="form-group">
                                <label htmlFor="seasonEndDate">Season End Date</label>
                                <p style={{ fontSize: 12, fontWeight: 'normal', color: 'grey' }}>Verified statuses expire at the end of this day</p>
                                <input type="date" id="seasonEndDate" name="seasonEndDate" className="form-control" style={{ maxWidth: 300 }} value={this.state.seasonEndDate} onChange={this.handleChange}/>
                            </div>
                            <div className="form-group">
                                <label htmlFor="rosterText">Roster</label>
                                <p style={{ fontSize: 12, fontWeight: 'normal', color: 'grey' }}>
                                    Upload a CSV file (columns: account number, email — a header row and extra columns are fine) or paste members below, one per line: accountNumber,email.
                                    The ACCOUNT NUMBER is what fans are matched on: Ticketmaster tells our server which account a fan's login belongs to, and we look for that number here.
                                    Include a header row naming that column (Acct ID, Account Number) so it is read correctly — without one, a wide export is ambiguous and the account is left out rather than guessed.
                                    Emails are stored too and will start matching if Ticketmaster begins returning members' addresses, but on their own they verify nobody today.
                                    Uploading REPLACES this team's whole roster — provide the complete list every time. Anyone who was verified but is missing from the new list loses their status,
                                    and everyone still on it picks up the season end date below.
                                </p>
                                <input
                                    type="file"
                                    accept=".csv,text/csv,text/plain"
                                    onChange={this.handleCsvFile}
                                    style={{ marginBottom: 10 }}
                                />
                                <textarea id="rosterText" name="rosterText" className="form-control" rows={8} value={this.state.rosterText} onChange={this.handleChange} placeholder={"10023456,fan@example.com\n10023457,another@example.com\n10023458"}/>
                            </div>
                            <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={this.uploadRoster}>Upload Roster</button>
                            {selectedMeta &&
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                                    <p style={{ fontSize: 12, fontWeight: 'normal', color: 'grey', margin: 0 }}>
                                        Current roster: {selectedMeta.rowCount || 0} members, uploaded {selectedMeta.uploadedAt ? convertTimeStampToHumanReadable(selectedMeta.uploadedAt) : 'unknown'}
                                    </p>
                                    {/* Until this, an uploaded roster could never be read back —
                                        the only way to drop one member was to re-upload the whole
                                        list from wherever it came from, blind. */}
                                    {(selectedMeta.rowCount || 0) > 0 &&
                                        <button type="button" className="btn btn-secondary btn-sm" onClick={this.viewRoster}>View list</button>
                                    }
                                </div>
                            }
                        </div>
                    }

                    {teamList.length > 0 &&
                        <div className="admin-form-box" style={{ maxWidth: 900 }}>
                            <p style={{ fontWeight: 'bold', fontSize: 16 }}>Roster status by team</p>
                            <div className="admin-table-wrap">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Team</th>
                                            <th>Status Label</th>
                                            <th>Matchable</th>
                                            <th>Uploaded</th>
                                            <th>Season End</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {teamList.map(exp => {
                                            const meta = (sthRoster[exp.id] || {}).meta;
                                            return (
                                                <tr key={exp.id}>
                                                    <td>{exp.name}</td>
                                                    <td>{meta ? (meta.label || '-') : <span style={{ color: 'grey' }}>No roster uploaded</span>}</td>
                                                    {/* Matchable = rows with an ACCOUNT NUMBER. A fan is recognised by
                                                        the identity Ticketmaster gives our server, and that identity
                                                        carries an account number — an email only matches if
                                                        Ticketmaster returns one. A roster of emails alone verifies
                                                        nobody, which is worth saying out loud rather than showing a
                                                        healthy-looking count. */}
                                                    <td>
                                                        {!meta ? '—' : (
                                                            (meta.accountCount || 0) > 0
                                                                ? `${meta.accountCount} of ${meta.rowCount || 0}`
                                                                : <span style={{ color: '#b3261e' }} title="This roster has no account-number column, so no fan can be matched by it. Re-export including the account/acct id column.">
                                                                    0 of {meta.rowCount || 0} — no account numbers
                                                                </span>
                                                        )}
                                                    </td>
                                                    <td>{meta && meta.uploadedAt ? convertTimeStampToHumanReadable(meta.uploadedAt) : '—'}</td>
                                                    <td>{meta && meta.expiresAt ? new Date(meta.expiresAt).toLocaleDateString() : '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    }

                    <div className="admin-form-box" style={{ maxWidth: 1000 }}>
                        <p style={{ fontWeight: 'bold', fontSize: 16 }}>Recent verification attempts</p>
                        <p style={{ fontSize: 12, fontWeight: 'normal', color: 'grey' }}>
                            The last 50 times a fan tapped Verify Status. When someone says it didn't work, the email here is
                            the one their Ticketmaster account presented — if it isn't the address on your roster, that's why.
                        </p>
                        {audit.length === 0 ? (
                            <p style={{ fontSize: 13, color: 'grey' }}>No attempts yet.</p>
                        ) : (
                            <div className="admin-table-wrap">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>When</th>
                                            <th>Ticketmaster email</th>
                                            <th>Result</th>
                                            <th>Account number(s)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {audit.map(entry => (
                                            <tr key={entry.id}>
                                                <td style={{ whiteSpace: 'nowrap' }}>{entry.at ? convertTimeStampToHumanReadable(entry.at) : '—'}</td>
                                                <td>{entry.claimedEmail || <span style={{ color: 'grey' }}>not reached</span>}</td>
                                                <td>{this.outcomeLabel(entry)}</td>
                                                <td style={{ fontSize: 12, color: 'grey' }}>{entry.tmAccounts || entry.tmMemberId || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                <Modal isOpen={this.state.rosterModal} toggle={this.toggleRosterModal} style={{ maxWidth: 720 }}>
                    <div style={{ padding: 20 }}>
                        <h4 style={{ marginTop: 0 }}>Season ticket roster</h4>
                        <p style={{ fontSize: 12, color: 'grey' }}>
                            {selectedTeamName ? `${selectedTeamName} — ` : ''}
                            {this.state.rosterMembers.length} member(s). Remove anyone who should no longer be
                            verified, then Save. Removing someone who is already verified takes their status away.
                        </p>
                        <input
                            type="text"
                            id="rosterFilter"
                            name="rosterFilter"
                            className="form-control"
                            style={{ marginBottom: 10 }}
                            value={this.state.rosterFilter}
                            onChange={this.handleChange}
                            placeholder="Search by account number or email"
                        />
                        {/* Long rosters are the normal case, so the list is capped and
                            the search is how you reach anyone past the first screenful. */}
                        <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4 }}>
                            <table className="table table-sm" style={{ margin: 0 }}>
                                <thead>
                                    <tr>
                                        <th>Account number</th>
                                        <th>Email</th>
                                        <th style={{ width: 90 }}/>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleRosterMembers.map(({ member, index }) => (
                                        <tr key={`${member.account}|${member.email}`}>
                                            <td>{member.account || <span style={{ color: '#b00' }}>none</span>}</td>
                                            <td>{member.email || <span style={{ color: 'grey' }}>—</span>}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => this.removeRosterMember(index)}
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {visibleRosterMembers.length === 0 &&
                                        <tr>
                                            <td colSpan={3} style={{ color: 'grey' }}>
                                                {this.state.rosterMembers.length === 0 ? 'No members left. Saving will clear this roster.' : 'Nobody matches that search.'}
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                        {hiddenRosterCount > 0 &&
                            <p style={{ fontSize: 12, color: 'grey', marginTop: 8 }}>
                                Showing {visibleRosterMembers.length} of {matchingRosterCount} matches. Search to narrow it down.
                            </p>
                        }
                        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                            <button type="button" className="btn btn-primary" onClick={this.saveRosterChanges}>Save changes</button>
                            <button type="button" className="btn btn-secondary" onClick={this.toggleRosterModal}>Cancel</button>
                        </div>
                    </div>
                </Modal>
            </div>
        );
    }
}

export default SetUpVerification;
